import { ProgramTypeVariants, DeliveryStatus, WarehouseChannelBadgeStatus } from "../../constants.js";
import { getProductNavigationUrl } from "../../productService/lib.js";
import { PROGRAM_TYPES_PRICE, TRAVEL_DESTINATION, PRODUCT_CLASS_TYPES } from "../LucidWorks/constants.js";
import { isBD } from "../../../utils/businessType.lib.js";
import { hasMembershipId } from "../../../utils/index.js";
const getIsFutureDate = (dateString) => {
  if (!dateString || typeof dateString !== "string") {
    return false;
  }
  const inputDate = new Date(dateString);
  const currentDate = /* @__PURE__ */ new Date();
  return inputDate > currentDate;
};
const getIsMemberOnlyData = (item) => {
  var _a, _b, _c;
  const itemMemberOnly = (_a = item.variantRollupValues["attributes.member_only"]) == null ? void 0 : _a[0];
  const dispPriceInCartOnly = (_c = (_b = item.product.attributes.disp_price_in_cart_only) == null ? void 0 : _b.numbers) == null ? void 0 : _c[0];
  return Boolean(itemMemberOnly) && dispPriceInCartOnly === 1;
};
const convertProgramTypesToLowercase = (programType) => {
  return new Set(
    programType == null ? void 0 : programType.map((pType) => pType.toString().toLocaleLowerCase())
  );
};
const isProgramTypeExists = (programTypes, programType) => {
  const toLowerCaseProgramTypeSet = convertProgramTypesToLowercase(programTypes);
  return toLowerCaseProgramTypeSet.has(programType.toLowerCase());
};
const getIsBuyable = (item) => {
  var _a, _b, _c, _d, _e;
  return !!(((_b = (_a = item.product.attributes.buyable) == null ? void 0 : _a.numbers) == null ? void 0 : _b[0]) && ((_e = (_d = (_c = item.product.variants[0]) == null ? void 0 : _c.attributes.buyable) == null ? void 0 : _d.numbers) == null ? void 0 : _e[0]));
};
const getIsChdiEligible = (item) => {
  var _a, _b, _c;
  return !!((_b = (_a = item.product.attributes.chdi_eligible) == null ? void 0 : _a.numbers) == null ? void 0 : _b[0]) || !!((_c = item.variantRollupValues["attributes.chdi_eligible"]) == null ? void 0 : _c.includes(1));
};
const getIsFsaEligible = (item) => {
  var _a, _b, _c;
  return !!((_b = (_a = item.product.attributes.fsa_eligible) == null ? void 0 : _a.numbers) == null ? void 0 : _b[0]) || !!((_c = item.variantRollupValues["attributes.fsa_eligible"]) == null ? void 0 : _c.includes(1));
};
const getIsBundleProductType = (productType) => {
  return Array.isArray(productType == null ? void 0 : productType.text) ? productType.text.some(
    (t) => typeof t === "string" && t.toLowerCase() === "bundle"
  ) : false;
};
const getDeliveryChannelBadgeData = (item, cookieContext) => {
  var _a, _b;
  const { userLocation } = cookieContext;
  const { city } = userLocation;
  const availability = (_a = item.inventory) == null ? void 0 : _a.deliveryAvailability;
  const isFutureDate = getIsFutureDate(
    (_b = item.variantRollupValues["attributes.start_date"]) == null ? void 0 : _b[0]
  );
  const isBuyable = getIsBuyable(item);
  if (!city || !availability) return null;
  if (isBuyable && isFutureDate) return DeliveryStatus.ComingSoon;
  if (isBuyable && !isFutureDate) {
    if (availability === "IN_STOCK" || availability === "LOW_STOCK") {
      return DeliveryStatus.Available;
    } else if (availability === "OUT_OF_STOCK") {
      return DeliveryStatus.OutOfStock;
    } else {
      return DeliveryStatus.Unavailable;
    }
  }
};
const getPillBadges = (pills, pillBadgeConfig, domainUrl) => {
  if (!pills || !pills.length) return null;
  const { defaultColor, colorMap, catalogSearchUrl } = pillBadgeConfig || {};
  return pills == null ? void 0 : pills.map((pill) => {
    let sanitizedPillValue = pill.replaceAll(
      "^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$",
      ""
    );
    sanitizedPillValue = sanitizedPillValue.replaceAll("[/\\\\]", "-");
    sanitizedPillValue = sanitizedPillValue.replaceAll("[^\\w\\s\\-]", "");
    sanitizedPillValue = sanitizedPillValue.replaceAll("[\\s\\-]+", "-");
    sanitizedPillValue = sanitizedPillValue.toLowerCase();
    const searchParams = encodeURIComponent(sanitizedPillValue);
    const pillBadgeLinkUrl = `${domainUrl}${catalogSearchUrl}${searchParams}`;
    const tranformedColorKey = (pill == null ? void 0 : pill.replace(/[\s]|[&/\\#,+()$~%.:*?<>{}]/g, "_")) || "";
    const color = (colorMap == null ? void 0 : colorMap[tranformedColorKey]) || defaultColor;
    return {
      color,
      link: pillBadgeLinkUrl.toString(),
      text: pill
    };
  });
};
const getProductActions = (item, featureToggleConfig, hasUserSignedId, cookieContext) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j;
  const {
    id,
    inventory,
    product,
    matchingVariantCount,
    variantRollupValues
  } = item || {};
  const { attributes } = product || {};
  const availability = inventory == null ? void 0 : inventory.deliveryAvailability;
  const dispPriceInCartOnly = (_b = (_a = attributes.disp_price_in_cart_only) == null ? void 0 : _a.numbers) == null ? void 0 : _b[0];
  const { wcMember } = cookieContext || {};
  const COMING_SOON_FEATURE_ENABLED = featureToggleConfig == null ? void 0 : featureToggleConfig.comingSoonBanner.featureOn;
  const PLP_STOCK_STATUS_DISPLAY_ENABLED = featureToggleConfig == null ? void 0 : featureToggleConfig.stockStatus.featureOn;
  const isBuyable = getIsBuyable(item);
  const hasSingleSku = matchingVariantCount === 1;
  const enableAddButton = isBuyable && Boolean(hasSingleSku);
  const isBundleProductType = getIsBundleProductType(
    (_d = (_c = item.product) == null ? void 0 : _c.attributes) == null ? void 0 : _d.product_type
  );
  const inStock = availability === "IN_STOCK";
  const isFutureDate = getIsFutureDate(
    (_e = variantRollupValues["attributes.start_date"]) == null ? void 0 : _e[0]
  );
  const comingSoon = Boolean(COMING_SOON_FEATURE_ENABLED && isFutureDate);
  const deliveryStatusCheck = !PLP_STOCK_STATUS_DISPLAY_ENABLED || availability === "IN_STOCK" || availability === "LOW_STOCK";
  const isMemberOnlyData = getIsMemberOnlyData(item);
  if (isMemberOnlyData && !hasUserSignedId) {
    return { type: "SignInButton" };
  } else if ((dispPriceInCartOnly === 0 || hasUserSignedId && isMemberOnlyData && hasMembershipId((wcMember == null ? void 0 : wcMember.value) || "") || hasUserSignedId && !isMemberOnlyData) && enableAddButton && inStock && !comingSoon && deliveryStatusCheck && !isBundleProductType) {
    return {
      contractIdentifier: isProgramTypeExists(
        variantRollupValues["attributes.program_types"],
        ProgramTypeVariants.TWO_DAY_DELIVERY
      ) ? "Regional" : void 0,
      itemNumber: ((_f = variantRollupValues.variantId) == null ? void 0 : _f[0]) || "",
      maxQuantity: (_h = (_g = attributes.max_item_order_qty) == null ? void 0 : _g.text) == null ? void 0 : _h[0],
      minQuantity: (_j = (_i = attributes.min_item_order_qty) == null ? void 0 : _i.text) == null ? void 0 : _j[0],
      partNumber: id,
      type: "QuantitySelector"
    };
  } else if (isBD() && !inStock && isBuyable) {
    return { type: "OutOfStock" };
  } else if ((!inStock || !deliveryStatusCheck) && isBuyable) {
    return { type: "SeeDetails" };
  } else {
    return { type: "SelectOptions" };
  }
};
const getProductDeliveryBadgeData = (item, translationsConfig, baseUrl) => {
  const { variantRollupValues } = item || {};
  const { urls } = translationsConfig || {};
  const checkTwoDayDeliveryExists = isProgramTypeExists(
    variantRollupValues["attributes.program_types"],
    ProgramTypeVariants.TWO_DAY_DELIVERY
  );
  const checkCostcoGroceryExists = isProgramTypeExists(
    variantRollupValues["attributes.program_types"],
    ProgramTypeVariants.COSTCOGROCERY
  );
  if (baseUrl && urls && (checkTwoDayDeliveryExists || checkCostcoGroceryExists)) {
    return {
      linkUrl: `${baseUrl}${(urls == null ? void 0 : urls.groceryURL) || ""}`
    };
  }
  return null;
};
const getReviewRatingData = (item, translationsObj) => {
  var _a, _b, _c, _d;
  const isReviewsEligible = !!((_b = (_a = item.product.attributes.reviews_eligible) == null ? void 0 : _a.numbers) == null ? void 0 : _b[0]);
  const itemReviewRatings = (_c = item.product.rating) == null ? void 0 : _c.averageRating;
  const itemProductReviewCount = (_d = item.product.rating) == null ? void 0 : _d.ratingCount;
  const { translations } = translationsObj || {};
  if (isReviewsEligible && translations) {
    const parsedRating = (itemReviewRatings || 0).toFixed(2);
    const mapObj = {
      "{rating}": parsedRating,
      "{total}": `${itemProductReviewCount || 0}`
    };
    const ariaLabelText = translations == null ? void 0 : translations.averageratingarialabel.replace(
      /{rating}|{total}/gi,
      (matched) => mapObj[matched]
    );
    return {
      ariaLabel: +parsedRating ? ariaLabelText : translations == null ? void 0 : translations.noreviewsarialabel,
      reviewCount: itemProductReviewCount || 0,
      value: +parsedRating
    };
  }
  return null;
};
const getCompareProductButtonData = (item) => {
  var _a, _b;
  const itemProductComparable = !!((_b = (_a = item.product.attributes.comparable) == null ? void 0 : _a.numbers) == null ? void 0 : _b[0]);
  const isTwoDayDeliveryKeyExists = isProgramTypeExists(
    item.variantRollupValues["attributes.program_types"],
    ProgramTypeVariants.TWO_DAY_DELIVERY
  );
  return !isTwoDayDeliveryKeyExists && itemProductComparable;
};
const getSwatchProperties = (swatchItem) => {
  var _a;
  const swatchProperties = (_a = swatchItem == null ? void 0 : swatchItem.toString()) == null ? void 0 : _a.split("|");
  if ((swatchProperties == null ? void 0 : swatchProperties.length) >= 6) {
    return {
      swatchImage: swatchProperties[1],
      swatchValue: swatchProperties[3],
      swatchAttribute: swatchProperties[4],
      swatchPrimaryImage: swatchProperties[5]
    };
  }
  return void 0;
};
const getColorSwatchesData = (item, imageConfig, featureToggleConfig) => {
  var _a, _b;
  const id = item.id;
  const swatchInfo = item.variantRollupValues["attributes.swatch_info"];
  const productName = item.product.title;
  const productSwatchable = !!((_b = (_a = item.product.attributes.swatchable) == null ? void 0 : _a.numbers) == null ? void 0 : _b[0]);
  if (!productSwatchable) return null;
  const colorSwatchesData = [];
  swatchInfo == null ? void 0 : swatchInfo.forEach((swatchItem) => {
    var _a2;
    const swatchProperties = getSwatchProperties(swatchItem);
    if (!swatchProperties) return;
    const {
      swatchImage,
      swatchValue,
      swatchAttribute,
      swatchPrimaryImage
    } = swatchProperties;
    colorSwatchesData == null ? void 0 : colorSwatchesData.push({
      attributeValue: swatchValue || "",
      id: swatchValue || "",
      label: swatchAttribute || "",
      pdpNavigationUrl: getProductNavigationUrl(
        productName || "",
        id || "",
        imageConfig.domainUrl,
        swatchValue,
        swatchAttribute,
        (_a2 = featureToggleConfig == null ? void 0 : featureToggleConfig.enableBModPDPNavigationUrl) == null ? void 0 : _a2.isEnabled
      ),
      productImageUrl: swatchPrimaryImage || "",
      swatchImageUrl: swatchImage || ""
    });
  });
  return (colorSwatchesData == null ? void 0 : colorSwatchesData.length) ? colorSwatchesData : null;
};
const getProductImageUrl = (item) => {
  var _a, _b, _c, _d;
  const primaryImage = (_b = (_a = item.product.attributes.primary_image) == null ? void 0 : _a.text) == null ? void 0 : _b[0];
  const productSwatchable = !!((_d = (_c = item.product.attributes.swatchable) == null ? void 0 : _c.numbers) == null ? void 0 : _d[0]);
  if (!productSwatchable) return primaryImage;
  const swatchInfo = item.variantRollupValues["attributes.swatch_info"];
  if (!swatchInfo) return primaryImage;
  const swatchProperties = getSwatchProperties(swatchInfo == null ? void 0 : swatchInfo[0]);
  if (!swatchProperties) return primaryImage;
  const { swatchPrimaryImage } = swatchProperties;
  return swatchPrimaryImage || primaryImage;
};
const getProductUrl = (product) => {
  var _a;
  return product.uri || ((_a = product.variants[0]) == null ? void 0 : _a.uri);
};
const getReplacementItemNumber = () => {
  return false;
};
const getInWareHouseChannelBadgeData = (item, enabledWarehousesListConfig, featureToggleConfig, cookieContext, showMultiVariantsAvailability) => {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  const { warehouse, userLocation } = cookieContext || {};
  const { nearestWarehouse } = warehouse;
  const { city } = userLocation;
  const { bopiwUpdatesSync } = featureToggleConfig || {};
  const isEnableBOPIWUpdatesSync = Boolean(bopiwUpdatesSync == null ? void 0 : bopiwUpdatesSync.featureOn);
  const isWarehouseEnabled = (_a = enabledWarehousesListConfig == null ? void 0 : enabledWarehousesListConfig.enabledWarehousesList) == null ? void 0 : _a.some(
    (enabledWarehouseId) => enabledWarehouseId === (nearestWarehouse == null ? void 0 : nearestWarehouse.catalog)
  );
  const lwxBOPIWWarehouseEnabled = (_b = enabledWarehousesListConfig == null ? void 0 : enabledWarehousesListConfig.enabledWarehousesBOPIWList) == null ? void 0 : _b.some(
    (enabledWarehouseId) => enabledWarehouseId === (nearestWarehouse == null ? void 0 : nearestWarehouse.catalog)
  );
  const lwxWIOWarehouseEnabled = (_c = enabledWarehousesListConfig == null ? void 0 : enabledWarehousesListConfig.enabledWarehousesWIOList) == null ? void 0 : _c.some(
    (enabledWarehouseId) => enabledWarehouseId === (nearestWarehouse == null ? void 0 : nearestWarehouse.catalog)
  );
  const { attributes } = item.product;
  const contentType = (_d = attributes.product_type) == null ? void 0 : _d.text[0];
  const isAVarriantAttr = [];
  const inWarehouseStatus = (_e = item.inventory) == null ? void 0 : _e.warehouseAvailability;
  const itemProductImageSwatchable = Boolean(
    (_f = attributes.swatchable) == null ? void 0 : _f.numbers[0]
  );
  const hasSingleSku = getHasSingleSku(item);
  const deliveryTypes = (_g = attributes.delivery_type) == null ? void 0 : _g.text;
  const deliveryStatus = (_h = item.inventory) == null ? void 0 : _h.deliveryAvailability;
  const inWarehouseProgramType = isProgramTypeExists(
    item.variantRollupValues["attributes.program_types"],
    ProgramTypeVariants.IN_WAREHOUSE
  );
  const itemLocationBopiw = inWarehouseProgramType && inWarehouseStatus !== "OUT_OF_STOCK" && (deliveryStatus === "IN_STOCK" || deliveryStatus === "OUT_OF_STOCK" || deliveryStatus === "LOW_STOCK");
  if (!isWarehouseEnabled || !city || (contentType == null ? void 0 : contentType.includes(TRAVEL_DESTINATION)) || isAVarriantAttr && isAVarriantAttr.length > 0) {
    return null;
  }
  const shipToWarehouseEnabled = (deliveryTypes == null ? void 0 : deliveryTypes.includes("Warehouse Pick-up")) || (deliveryTypes == null ? void 0 : deliveryTypes.includes("Cueillette à l’entrepôt"));
  const bopiwProgramType = isProgramTypeExists(
    item.variantRollupValues["attributes.program_types"],
    ProgramTypeVariants.USE_WAREHOUSE_INVENTORY
  );
  const itemProgramType = !!inWarehouseStatus;
  if (isWarehouseEnabled && isEnableBOPIWUpdatesSync) {
    if (showMultiVariantsAvailability && (itemProgramType && itemProductImageSwatchable || !hasSingleSku)) {
      if (bopiwProgramType) {
        return {
          status: WarehouseChannelBadgeStatus.SEE_PRODUCT_DETAILS,
          warehouse: city
        };
      } else {
        return null;
      }
    }
    if (itemLocationBopiw || itemProgramType && inWarehouseStatus === "IN_STOCK" || inWarehouseStatus === "LOW_STOCK") {
      if (itemLocationBopiw && bopiwProgramType && inWarehouseStatus === "IN_STOCK") {
        return {
          status: WarehouseChannelBadgeStatus.IN_STOCK_PICK_UP,
          warehouse: city
        };
      }
      if (itemLocationBopiw && bopiwProgramType && inWarehouseStatus === "LOW_STOCK") {
        return {
          status: WarehouseChannelBadgeStatus.LOW_STOCK_IN_WAREHOUSE,
          warehouse: city
        };
      }
      if (itemLocationBopiw && (deliveryStatus === "IN_STOCK" || deliveryStatus === "LOW_STOCK") && shipToWarehouseEnabled) {
        return {
          status: WarehouseChannelBadgeStatus.SHIPS_TO_WAREHOUSE,
          warehouse: city
        };
      }
      return null;
    }
    if (itemProgramType && inWarehouseStatus === "OUT_OF_STOCK") {
      if (itemLocationBopiw && shipToWarehouseEnabled) {
        if (deliveryStatus === "IN_STOCK" || deliveryStatus === "LOW_STOCK") {
          return {
            status: WarehouseChannelBadgeStatus.SHIPS_TO_WAREHOUSE,
            warehouse: city
          };
        }
      } else if (bopiwProgramType) {
        return {
          status: WarehouseChannelBadgeStatus.OUT_OF_STOCK,
          warehouse: city
        };
      }
    }
    return null;
  }
  if (isWarehouseEnabled) {
    if (showMultiVariantsAvailability && (inWarehouseStatus && itemProductImageSwatchable || !hasSingleSku)) {
      if (lwxBOPIWWarehouseEnabled && bopiwProgramType || inWarehouseStatus && lwxWIOWarehouseEnabled) {
        return {
          status: WarehouseChannelBadgeStatus.SEE_PRODUCT_DETAILS,
          warehouse: city,
          ...lwxWIOWarehouseEnabled && {
            showPriceMayVaryText: true
          }
        };
      } else {
        return null;
      }
    }
    if (bopiwProgramType && shipToWarehouseEnabled) {
      if (itemLocationBopiw && inWarehouseStatus === "IN_STOCK" && lwxBOPIWWarehouseEnabled) {
        return {
          status: WarehouseChannelBadgeStatus.IN_STOCK_PICK_UP,
          warehouse: city
        };
      }
      if (!itemLocationBopiw && (deliveryStatus === "IN_STOCK" || deliveryStatus === "LOW_STOCK")) {
        return {
          status: WarehouseChannelBadgeStatus.SHIPS_TO_WAREHOUSE,
          warehouse: city
        };
      }
      return null;
    }
    if (bopiwProgramType) {
      if (itemLocationBopiw && inWarehouseStatus === "IN_STOCK" && lwxBOPIWWarehouseEnabled) {
        return {
          status: WarehouseChannelBadgeStatus.IN_STOCK_PICK_UP,
          warehouse: city
        };
      }
      if (!itemLocationBopiw && inWarehouseStatus === "LOW_STOCK") {
        return {
          status: WarehouseChannelBadgeStatus.LOW_STOCK_IN_WAREHOUSE,
          warehouse: city,
          ...lwxWIOWarehouseEnabled && {
            showPriceMayVaryText: true
          }
        };
      }
      if (!itemLocationBopiw && inWarehouseProgramType && inWarehouseStatus === "IN_STOCK") {
        return {
          showPriceMayVaryText: true,
          status: WarehouseChannelBadgeStatus.IN_STOCK_IN_WAREHOUSE,
          warehouse: city
        };
      }
      if (!itemLocationBopiw && inWarehouseStatus === "OUT_OF_STOCK") {
        return {
          status: WarehouseChannelBadgeStatus.OUT_OF_STOCK,
          warehouse: city
        };
      }
      return null;
    }
    if (shipToWarehouseEnabled) {
      if (deliveryStatus === "IN_STOCK" || deliveryStatus === "LOW_STOCK") {
        return {
          status: WarehouseChannelBadgeStatus.SHIPS_TO_WAREHOUSE,
          warehouse: city
        };
      }
      return null;
    }
    if (itemProgramType) {
      if (inWarehouseStatus === "IN_STOCK" && inWarehouseProgramType && lwxWIOWarehouseEnabled) {
        return {
          showPriceMayVaryText: true,
          status: WarehouseChannelBadgeStatus.IN_STOCK_IN_WAREHOUSE,
          warehouse: city
        };
      }
      if (inWarehouseStatus === "LOW_STOCK" && lwxWIOWarehouseEnabled) {
        return {
          status: WarehouseChannelBadgeStatus.LOW_STOCK_IN_WAREHOUSE,
          warehouse: city,
          ...lwxWIOWarehouseEnabled && {
            showPriceMayVaryText: true
          }
        };
      }
      if (inWarehouseStatus === "OUT_OF_STOCK" && lwxWIOWarehouseEnabled) {
        return {
          status: WarehouseChannelBadgeStatus.OUT_OF_STOCK,
          warehouse: city
        };
      }
      return null;
    }
  }
  return null;
};
const getIsBopimEligible = (item) => {
  var _a;
  const itemProgramBopim = isProgramTypeExists(
    item.variantRollupValues["attributes.program_types"],
    ProgramTypeVariants.LTL_PICKUP
  );
  const inStock = item.inventory.mdoAvailability === "IN_STOCK";
  const isMdoLocationPickupEligible = !!((_a = item.mdoLocation) == null ? void 0 : _a.isPickupEligible);
  return itemProgramBopim && inStock && isMdoLocationPickupEligible;
};
const getAvailabilityInfoData = (item, enableWarehouseListConfig, featureToggleConfig, cookieContext, showMultiVariantsAvailability) => {
  return {
    bopimStatus: getIsBopimEligible(item),
    inWarehouseAvailabilityText: isProgramTypeExists(
      item.variantRollupValues["attributes.program_types"],
      ProgramTypeVariants.TWO_DAY_DELIVERY
    ),
    inWarehouseChannelBadgeData: getInWareHouseChannelBadgeData(
      item,
      enableWarehouseListConfig,
      featureToggleConfig,
      cookieContext,
      showMultiVariantsAvailability
    )
  };
};
const getHasSingleSku = (item) => item.matchingVariantCount === 1;
const getAddToListButtonData = (item, cookieContent) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
  const hasSingleSku = getHasSingleSku(item);
  const item_collateral_primaryimage = (_b = (_a = item.product.attributes["primary_image"]) == null ? void 0 : _a.text) == null ? void 0 : _b[0];
  const item_product_name = item.product.title;
  const item_location_availability = item.inventory.deliveryAvailability;
  const item_backorderflag = item.inventory.deliveryAvailability !== "OUT_OF_STOCK";
  const item_backorderqty = item.inventory.deliveryAvailability === "BACK_ORDER";
  const item_product_buyable = (_d = (_c = item.product.attributes["buyable"]) == null ? void 0 : _c.numbers) == null ? void 0 : _d[0];
  const item_buyable = (_f = (_e = item.product.attributes["buyable"]) == null ? void 0 : _e.numbers) == null ? void 0 : _f[0];
  const content_type = (_h = (_g = item.product.attributes["product_type"]) == null ? void 0 : _g.text) == null ? void 0 : _h[0];
  const isBundleProductType = getIsBundleProductType(
    (_j = (_i = item.product) == null ? void 0 : _i.attributes) == null ? void 0 : _j.product_type
  );
  const item_classification_itemclass = (_l = (_k = item.product.attributes["product_class_type"]) == null ? void 0 : _k.text) == null ? void 0 : _l[0];
  const price = getPriceData(item, cookieContent);
  const inStock = item_location_availability !== "OUT_OF_STOCK" || item_backorderflag && item_backorderqty;
  const isPurchasable = item_product_buyable && item_buyable;
  const hideListButton = Boolean(
    item_classification_itemclass && PRODUCT_CLASS_TYPES.includes(item_classification_itemclass)
  );
  if (hasSingleSku && (inStock && isPurchasable || !inStock && !isPurchasable) && !hideListButton && !(content_type == null ? void 0 : content_type.includes(TRAVEL_DESTINATION)) && !isBundleProductType) {
    return {
      image: item_collateral_primaryimage,
      name: item_product_name,
      price: (price == null ? void 0 : price.priceValue) || null
    };
  }
  return null;
};
const getMemberOnlyData = (item) => {
  var _a, _b, _c;
  return Boolean((_a = item.variantRollupValues["attributes.member_only"]) == null ? void 0 : _a[0]) && ((_c = (_b = item.product.attributes["disp_price_in_cart_only"]) == null ? void 0 : _b.numbers) == null ? void 0 : _c[0]) === 1;
};
const getPriceData = (item, cookieContent) => {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  const { wcMember } = cookieContent;
  if (!hasMembershipId(wcMember.value) && getMemberOnlyData(item))
    return null;
  const programTypes = item.variantRollupValues["attributes.program_types"] || [];
  const minSalePrice = Number(((_b = (_a = item.inventory) == null ? void 0 : _a.deliveryPrice) == null ? void 0 : _b.minPrice) || 0);
  const maxSalePrice = Number(((_d = (_c = item.inventory) == null ? void 0 : _c.deliveryPrice) == null ? void 0 : _d.maxPrice) || 0);
  const item_product_price_in_cart_only = ((_f = (_e = item.product.attributes["disp_price_in_cart_only"]) == null ? void 0 : _e.numbers) == null ? void 0 : _f[0]) || 0;
  const hasSingleSku = item.matchingVariantCount === 1;
  const Hide_Price_Range_attr = (_h = (_g = item.product.attributes["hide_price_range"]) == null ? void 0 : _g.text) == null ? void 0 : _h[0];
  const toLowerCaseProgramTypeSet = convertProgramTypesToLowercase(programTypes);
  const programTypeCheck = isBD() ? true : PROGRAM_TYPES_PRICE.some(
    (type) => toLowerCaseProgramTypeSet.has(type.toLocaleLowerCase())
  );
  if (programTypeCheck && item_product_price_in_cart_only !== 2 && minSalePrice) {
    if (!hasSingleSku) {
      if (Hide_Price_Range_attr === "1" && maxSalePrice && minSalePrice < maxSalePrice) {
        return {
          priceText: "Starting at",
          priceValue: minSalePrice
        };
      } else if (minSalePrice === maxSalePrice) {
        return {
          priceText: null,
          priceValue: minSalePrice
        };
      } else {
        return {
          priceText: null,
          priceValue: [minSalePrice, maxSalePrice]
        };
      }
    } else {
      return {
        priceText: null,
        priceValue: minSalePrice
      };
    }
  }
  return null;
};
export {
  convertProgramTypesToLowercase,
  getAddToListButtonData,
  getAvailabilityInfoData,
  getColorSwatchesData,
  getCompareProductButtonData,
  getDeliveryChannelBadgeData,
  getInWareHouseChannelBadgeData,
  getIsBopimEligible,
  getIsBundleProductType,
  getIsBuyable,
  getIsChdiEligible,
  getIsFsaEligible,
  getIsFutureDate,
  getIsMemberOnlyData,
  getMemberOnlyData,
  getPillBadges,
  getPriceData,
  getProductActions,
  getProductDeliveryBadgeData,
  getProductImageUrl,
  getProductUrl,
  getReplacementItemNumber,
  getReviewRatingData,
  isProgramTypeExists
};
